import * as React from 'react';
import { Col, Row } from 'react-bootstrap';
import { compose } from 'redux';
import type { InjectedFormProps } from 'redux-form';
import { Field, reduxForm } from 'redux-form';

import * as TimeOptionUtils from '@acceligentllc/shared/utils/timeOption';

import { timezones } from '@acceligentllc/shared/enums/timezone';
import { weekDays } from '@acceligentllc/shared/enums/weekDay';

import { Button } from '@acceligentllc/storybook';

import Dropdown from 'af-fields/Dropdown';
import MultiselectDropdown from 'af-fields/MultiselectDropdown';

import Form from 'af-components/Form';

import { NotificationTimeSettingsFM } from './formModel';
import { TIMEZONE_FILTER_BY } from './helpers';
import styles from './styles.module.scss';

interface OwnProps extends Partial<InjectedFormProps> {
	onSubmit: (form: NotificationTimeSettingsFM) => Promise<void>;
	showDaySelector?: boolean;
}

type Props = OwnProps & InjectedFormProps<NotificationTimeSettingsFM>;

interface DayOption {
	id: number;
	label: string;
}

const options = weekDays.map<DayOption>((_day, _i) => ({ id: _i + 1, label: _day }));

const NotificationTimeSettingsForm: React.FC<Props> = (props) => {
	const [days, setDays] = React.useState<DayOption[]>([options[0]]);

	const {
		handleSubmit,
		onSubmit,
	} = props;

	const showDaySelector = props.showDaySelector ?? false;

	const renderOption = React.useCallback((option: DayOption) => {
		return (
			<div className={styles['dropdown-option']}>
				{option.label}
			</div>
		);
	}, []);

	const onValueChange = React.useCallback((_options: DayOption[]) => {
		setDays(_options);
	}, []);

	const renderSelected = React.useCallback((_selected: DayOption[]) => {
		if (_selected.length === 0) {
			return <b>{'None'}</b>;
		}

		const _days = _selected.sort((_a, _b) => _a.id - _b.id).map((_option) => _option.label[0]);

		return <b>{_days.join(', ')}</b>;
	}, []);

	return (
		<Form>
			<Form.Section>
				<div className={styles['time-settings-inputs']}>
					<Row>
						<Col sm={6}>
							<Field
								component={Dropdown}
								filterable={true}
								filterBy={TIMEZONE_FILTER_BY}
								id="timezone"
								label="Timezone"
								labelKey="name"
								name="timezone"
								options={timezones}
								valueKey="name"
								withCaret={true}
							/>
						</Col>
						<Col sm={6}>
							<Field
								component={Dropdown}
								fixed={true}
								id="notificationTime"
								label="At"
								labelKey="name"
								name="notificationTime"
								options={TimeOptionUtils.enumWithoutNullValue()}
								valueKey="id"
								withCaret={true}
							/>
						</Col>
						{showDaySelector &&
							<Col sm={6}>
								<Field
									component={MultiselectDropdown}
									defaultSelections={days}
									fixed={true}
									hasNoneAsOption={false}
									id="notificationDays"
									isMultiselect={true}
									label="On"
									labelKey="label"
									name="notificationDays"
									onValueChange={onValueChange}
									options={options}
									renderMenuItem={renderOption}
									renderSelected={renderSelected}
									valueKey="id"
									withCaret={true}
								/>
							</Col>
						}
					</Row>
				</div>
				<Row>
					<Button
						label="Save"
						onClick={handleSubmit(onSubmit)}
						style="primary"
					/>
				</Row>
			</Form.Section>
		</Form >
	);
};

const enhance = compose<React.ComponentType<OwnProps>>(
	reduxForm<NotificationTimeSettingsFM, OwnProps>({
		validate: NotificationTimeSettingsFM.validate,
		enableReinitialize: true,
	}),
	React.memo
);

export default enhance(NotificationTimeSettingsForm);
